import moment from 'moment';
import BN from 'bignumber.js';
import { React, PT, fetchApi } from '@gasbuddy/react';
import JSONTree from 'react-json-tree';
import { Button, Table, Popup, Icon, Menu, Form } from 'semantic-ui-react';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { GUTransactionCorrectionModal } from './GUTransactionCorrectionModal';

function couponUsesLeft(coupon) {
  return coupon.usage_limit - ((coupon.transaction_ids && coupon.transaction_ids.length) || 0);
}

const statusSortMap = {
  active: 0,
  upcoming: 1,
  consumed: 2,
  expired: 3,
};

function couponStatus(coupon) {
  if (couponUsesLeft(coupon) <= 0) {
    return 'consumed';
  }
  if (moment().isAfter(coupon.expires_at)) {
    return 'expired';
  }
  if (moment().isBefore(coupon.begins_at)) {
    return 'upcoming';
  }

  return 'active';
}

function couponRowAttributes(coupon) {
  const status = couponStatus(coupon);

  if (status === 'consumed' || status === 'expired') {
    return { error: true };
  }
  if (status === 'upcoming') {
    return { warning: true };
  }
  return {};
}

function amountString({ amount, discount_type: type }) {
  if (!amount) {
    return '-';
  }
  if (type === 'per_gallon') {
    return `${new BN(amount).times(100).toString()}¢/gal`;
  }
  return `$${new BN(amount).toFixed(2)}`;
}

function ShowPoi({ poi }) {
  const { name, address } = poi;
  const addr = [
    name, address.line_1, address.line_2, address.locality,
    address.region, address.postal_code, address.country,
  ].filter(a => a).join(', ');
  return (
    <div>
      <strong>POI: </strong>
      {addr}
    </div>
  );
}
ShowPoi.propTypes = {
  poi: PT.shape(
    {
      name: PT.string.isRequired,
      address: PT.shape({
        line_1: PT.string,
        line_2: PT.string,
        locality: PT.string,
        region: PT.string,
        country: PT.string,
        postal_code: PT.string,
      }).isRequired,
    },
  ).isRequired,
};

export class CouponsTable extends React.Component {
  state = { page: 0, pageSize: 5, copied: null, sort: { by: null, reverse: true }, poi: null }

  get currentPage() {
    const { page } = this.state;
    return page || 0;
  }

  nextPage = () => this.setState({ page: this.currentPage + 1, copied: null })

  prevPage = () => this.setState({ page: this.currentPage - 1, copied: null })

  sortBy(type) {
    const { sort: { by, reverse } } = this.state;
    const newSortBy = by === type && !reverse ? null : type;
    return this.setState({ sort: { by: newSortBy, reverse: newSortBy !== by } });
  }

  sortArrow(type) {
    const { sort: { by, reverse } } = this.state;
    if (!by) {
      return '↓↑';
    }
    if (by !== type) {
      return '';
    }
    return reverse ? '↓' : '↑';
  }

  sortCoupons(a, b) {
    const { sort: { by, reverse } } = this.state;
    const aBegin = a.begins_at || a.created_at;
    const bBegin = b.begins_at || b.created_at;
    const dir = reverse ? 1 : -1;
    switch (by) {
      case 'date':
        return (moment(bBegin).valueOf() - moment(aBegin).valueOf()) * dir;
      case 'name':
        return a.name.localeCompare(b.name) * dir;
      case 'amount':
        return (b.amount - a.amount) * dir;
      case 'usage':
        return (couponUsesLeft(b) - couponUsesLeft(a)) * dir;
      default:
        return (
          statusSortMap[couponStatus(a)] - statusSortMap[couponStatus(b)]
          || moment(bBegin).valueOf() - moment(aBegin).valueOf()
        ) * dir;
    }
  }

  pageSize() {
    const { pageSize } = this.state;
    return (
      <Form.Dropdown
        selection
        label={null}
        value={pageSize}
        style={{ minWidth: '0' }}
        options={[5, 10, 15, 20].map(v => ({ text: v, value: v }))}
        onChange={(e, { value }) => this.setState({ pageSize: value })}
      />
    );
  }

  filter() {
    const { filter } = this.state;
    const { coupons } = this.props;
    const names = ['No Filter'];
    coupons.forEach((c) => {
      if (!names.includes(c.name)) {
        names.push(c.name);
      }
    });
    return (
      <Form.Dropdown
        selection
        value={filter}
        icon="filter"
        style={{ minWidth: '0' }}
        options={names.map(v => ({ text: v, value: v }))}
        onChange={(e, { value }) => this.setState({ filter: value })}
      />
    );
  }

  render() {
    const { page, pageSize, copied, poi, filter } = this.state;
    const { accountId, coupons: baseCoupons } = this.props;
    let coupons = [...(baseCoupons || [])];
    coupons = coupons.filter(c => c.name === filter || filter === 'No Filter' || !filter);
    coupons.sort((a, b) => this.sortCoupons(a, b));
    const offset = page * pageSize;
    const couponPage = (coupons || []).slice(offset, offset + pageSize);
    const showPrevPageButton = page > 0;
    const showNextPageButton = coupons && coupons.length > (pageSize * (page + 1));
    const range = `Coupons ${offset + 1}-${offset + couponPage.length} of ${(coupons || []).length}`;

    return (
      <Table basic="very" selectable style={{ fontSize: '0.9em' }}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={6} onClick={() => this.sortBy('date')}>
              {`Date${this.sortArrow('date')}`}
            </Table.HeaderCell>
            <Table.HeaderCell width={6} onClick={() => this.sortBy('name')}>
              {`Name${this.sortArrow('name')}`}
              {this.filter()}
            </Table.HeaderCell>
            <Table.HeaderCell width={4} onClick={() => this.sortBy('usage')}>
              {`Uses Left${this.sortArrow('usage')}`}
            </Table.HeaderCell>
            <Table.HeaderCell onClick={() => this.sortBy('amount')}>
              {`Amount${this.sortArrow('amount')}`}
            </Table.HeaderCell>
            <Table.HeaderCell>Txs</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {couponPage.map(c => (
            <Popup
              key={c.account_coupon_id}
              wide="very"
              on="click"
              trigger={(
                <Table.Row {...couponRowAttributes(c)}>
                  <Table.Cell>{moment(c.begins_at || c.created_at).format('M/D/Y')}</Table.Cell>
                  <Table.Cell>
                    {c.name}
                    {c.name === 'GetUpside Offer'
                      ? (
                        <GUTransactionCorrectionModal
                          accountId={accountId}
                          coupon={c}
                        />
                      )
                      : null
                    }
                  </Table.Cell>
                  <Table.Cell textAlign="center">{couponUsesLeft(c)}</Table.Cell>
                  <Table.Cell>{amountString(c)}</Table.Cell>
                  <Table.Cell>
                    {[...(c.transaction_ids || []), ...(c.assignments || []).map(a => a.transaction_id)]
                      .map((txId, ix) => {
                        const link = (<a href={`/payments/transaction/${accountId}/${txId}`}>{txId}</a>);
                        if (ix > 0) {
                          return [', ', link];
                        }
                        return [link];
                      })}
                  </Table.Cell>
                </Table.Row>
              )}
              header="Coupon Detail"
              onOpen={async () => {
                const { poi_id: poiId } = c.extra_data;
                if (!poiId) {
                  return;
                }
                const { body, status } = await fetchApi({
                  url: `/poi/details/${poiId}/`,
                  method: 'GET',
                });
                if (status === 200) {
                  this.setState({ poi: body });
                } else {
                  this.setState({ poi: { name: 'Not found', address: {} } });
                }
              }}
              onClose={() => {
                this.setState({ poi: null });
              }}
              content={(
                <div style={{ minWidth: 500 }}>
                  <JSONTree
                    inverted
                    shouldExpandNode={() => true}
                    data={c}
                  />
                  <div>
                    <CopyToClipboard
                      text={JSON.stringify(c, null, 2)}
                      onCopy={() => this.setState({ copied: `coupon-${c.account_coupon_id}` })}
                      options={{ format: 'text/plain' }}
                    >
                      <Button toggle icon active={copied === `coupon-${c.account_coupon_id}`} labelPosition="left">
                        <Icon name={copied === `coupon-${c.account_coupon_id}` ? 'clipboard check' : 'clipboard'} />
                        {copied === `coupon-${c.account_coupon_id}` ? 'JSON copied to clipboard!' : 'Copy JSON'}
                      </Button>
                    </CopyToClipboard>
                    { poi && <ShowPoi poi={poi} /> }
                  </div>
                </div>
              )}
            />
          ))}
        </Table.Body>
        <Table.Footer>
          <Table.Row>
            <Table.Cell colSpan={3}>
              {range}
            </Table.Cell>
            <Table.Cell textAlign="right">
              {this.pageSize()}
            </Table.Cell>
            <Table.Cell textAlign="right">
              {(showPrevPageButton || showNextPageButton)
                && (
                  <Menu pagination>
                    {showPrevPageButton && (
                      <Menu.Item as="a" icon onClick={this.prevPage}>
                        <Icon name="left chevron" />
                      </Menu.Item>
                    )}
                    {showNextPageButton && (
                      <Menu.Item as="a" icon onClick={this.nextPage}>
                        <Icon name="right chevron" />
                      </Menu.Item>
                    )}
                  </Menu>
                )
              }
            </Table.Cell>
          </Table.Row>
        </Table.Footer>
      </Table>
    );
  }
}

CouponsTable.propTypes = {
  accountId: PT.number.isRequired,
  coupons: PT.arrayOf(PT.shape({
    account_coupon_id: PT.number,
    coupon_id: PT.number,
    usage_limit: PT.number,
    begins_at: PT.string,
    expires_at: PT.string,
    transaction_ids: PT.arrayOf(PT.number),
    eligibility: PT.object,
    name: PT.string,
    discount_type: PT.string,
    amount: PT.string,
  })),
};

CouponsTable.defaultProps = {
  coupons: [],
};
